import { NavLink, Outlet } from 'react-router-dom'
import { cn } from '@/internationalization/cn'
import { useT } from '@/internationalization/i18n'
import { PageHeader } from '@/components/domain'
import { PERMISSIONS, type Permission } from '@/features/authorization/permissions'
import { useSessionStore } from '@/stores'

/**
 * PERMISSIONS, NOT ROLES. These used to read `roles: ['owner', 'admin']`, which
 * after migration 028 asserts nothing: a customer's `admin` may hold more than
 * their `owner` did, or less. Naming the permission also means a customer who
 * moves a capability between roles gets a correct tab strip with no release.
 *
 * `profile` is ungated on purpose — everybody must be able to change their own
 * name, so Settings always has at least one pane and the sidebar entry is never
 * a dead end.
 */
const TABS: { to: string; label: string; permissions?: Permission[] }[] = [
  { to: '/settings/profile', label: 'Your profile' },
  { to: '/settings/workspace', label: 'Workspace', permissions: [PERMISSIONS.workspaceRead] },
  { to: '/settings/members', label: 'Members', permissions: [PERMISSIONS.memberRead] },
  { to: '/settings/roles', label: 'Roles', permissions: [PERMISSIONS.memberRead] },
  { to: '/settings/patrons', label: 'Patrons', permissions: [PERMISSIONS.patronManage] },
  { to: '/settings/recording', label: 'Recording rules', permissions: [PERMISSIONS.ruleManage] },
  { to: '/settings/languages', label: 'Languages', permissions: [PERMISSIONS.languageRead] },
  { to: '/settings/notifications', label: 'Notifications', permissions: [PERMISSIONS.notificationManage] },
  { to: '/settings/billing', label: 'Billing', permissions: [PERMISSIONS.billingRead] },
  { to: '/settings/security', label: 'Security & data', permissions: [PERMISSIONS.securityManage] },
]

/**
 * Settings is a section, not a page: a left rail on lg+, a horizontally
 * scrolling tab strip below it. Each pane is its own route so a link to
 * "recording rules" is shareable.
 */
export function SettingsLayout() {
  const t = useT()
  // Fails closed: null until the session is established, so a gated tab is
  // hidden rather than flashed.
  const permissions = useSessionStore((s) => s.permissions)
  const tabs = TABS.filter(
    (tab) => !tab.permissions || tab.permissions.every((p) => permissions?.includes(p)),
  )

  return (
    <div>
      <PageHeader title={t('settings.title')} description={t('settings.description')} />
      <div className="flex flex-col gap-6 lg:flex-row">
        <nav aria-label={t('settings.nav')} className="lg:w-56 lg:shrink-0">
          <ul className="no-scrollbar -mx-1 flex gap-1 overflow-x-auto px-1 lg:mx-0 lg:flex-col lg:px-0">
            {tabs.map((tab) => (
              <li key={tab.to} className="shrink-0 lg:w-full">
                <NavLink
                  to={tab.to}
                  className={({ isActive }) =>
                    cn(
                      'block whitespace-nowrap rounded-md px-3 py-2 text-base font-medium transition-colors',
                      isActive
                        ? 'bg-brand-subtle text-brand'
                        : 'text-fg-muted hover:bg-surface-muted hover:text-fg',
                    )
                  }
                >
                  {t(`settings.tab.${tab.to.split('/').pop()}`)}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <div className="min-w-0 flex-1">
          <Outlet />
        </div>
      </div>
    </div>
  )
}
